const required = (key, label, step, when = () => true) => ({ key, label, step, when });

export const PRODUCT_RULES = {
  whole_turnover: [
    required('annualTurnover', 'Annual turnover', 1),
    required('annualInsurableTurnover', 'Insurable credit turnover', 1),
    required('domesticSalesPercent', 'Domestic sales percentage', 1),
    required('exportSalesPercent', 'Export sales percentage', 1),
    required('exportCountries', 'Main export countries', 1, (f) => Number(f.exportSalesPercent) > 0),
    required('maxPaymentTerms', 'Maximum payment terms', 1),
    required('creditControlProcess', 'Credit-control process', 1),
    required('financialYearEnd', 'Financial year end', 2),
    required('totalAssets', 'Total assets', 2),
    required('totalLiabilities', 'Total liabilities', 2),
  ],
  single_risk: [
    required('companyId', 'Buyer', 1),
    required('requestedLimit', 'Requested limit', 1),
    required('goodsServices', 'Goods or services supplied', 1),
    required('paymentTerms', 'Payment terms', 2),
    required('relationshipStartDate', 'Trading relationship start date', 2),
    required('outstandingInvoices', 'Outstanding invoices', 2),
    required('hasDispute', 'Dispute status', 2),
    required('disputeDetails', 'Dispute details', 2, (f) => f.hasDispute === 'yes'),
  ],
  top_up: [
    required('companyId', 'Buyer', 1),
    required('existingCarrier', 'Existing carrier', 1),
    required('existingPolicyNumber', 'Policy number', 1),
    required('existingLimit', 'Existing approved limit', 1),
    required('carrierDecision', 'Carrier decision', 1),
    required('carrierDecisionDate', 'Carrier decision date', 1),
    required('requestedLimit', 'Additional limit requested', 2),
    required('topUpReason', 'Reason for top-up', 2),
    required('topUpExpiry', 'Required expiry date', 2),
  ],
  surety: [
    required('bondType', 'Bond type', 1),
    required('beneficiary', 'Beneficiary', 1),
    required('contractValue', 'Contract value', 1),
    required('bondAmount', 'Bond amount', 1),
    required('contractStartDate', 'Contract start date', 1),
    required('contractEndDate', 'Contract end date', 1),
    required('jurisdiction', 'Governing jurisdiction', 1),
    required('advancePaymentPercent', 'Advance-payment percentage', 1, (f) => f.bondType === 'Advance-payment bond'),
    required('annualTurnover', 'Annual turnover', 2),
    required('totalAssets', 'Total assets', 2),
    required('totalLiabilities', 'Total liabilities', 2),
    required('workInProgressValue', 'Work-in-progress value', 2),
    required('contractBacklog', 'Contract backlog', 2),
  ],
};

const numericFields = new Set([
  'annualTurnover', 'annualInsurableTurnover', 'requestedLimit', 'existingLimit', 'contractValue',
  'bondAmount', 'totalAssets', 'totalLiabilities', 'outstandingInvoices', 'workInProgressValue', 'contractBacklog',
]);
const percentageFields = new Set(['domesticSalesPercent', 'exportSalesPercent', 'advancePaymentPercent']);

export function validateInsuranceProduct(form, step = null) {
  const rules = PRODUCT_RULES[form.product] || [];
  const errors = rules.filter((rule) => (step === null || rule.step === step) && rule.when(form)).reduce((result, rule) => {
    if (form[rule.key] === undefined || form[rule.key] === null || String(form[rule.key]).trim() === '') result[rule.key] = `${rule.label} is required`;
    return result;
  }, {});
  for (const field of [...numericFields, ...percentageFields]) {
    if ((step !== null && !rules.some((rule) => rule.step === step && rule.key === field)) || form[field] === '' || form[field] === undefined) continue;
    if (!Number.isFinite(Number(form[field])) || Number(form[field]) < 0) errors[field] = 'Enter a valid non-negative number';
  }
  for (const field of percentageFields) {
    if (form[field] !== '' && form[field] !== undefined && Number(form[field]) > 100) errors[field] = 'Percentage cannot exceed 100';
  }
  if (form.product === 'whole_turnover' && Number(form.domesticSalesPercent || 0) + Number(form.exportSalesPercent || 0) !== 100) {
    if (step === null || step === 1) errors.salesMix = 'Domestic and export sales must total 100%';
  }
  if (form.product === 'surety' && form.contractStartDate && form.contractEndDate && new Date(form.contractEndDate) <= new Date(form.contractStartDate)) {
    if (step === null || step === 1) errors.contractEndDate = 'Contract end date must be after its start date';
  }
  return errors;
}

export function missingProductRequirements(form) {
  const errors = validateInsuranceProduct(form);
  return [...new Set(Object.entries(errors).map(([key, message]) => key === 'salesMix' ? message : message.replace(/ is required$/, '')))];
}
